// frontend/src/components/detail/RelatedInsights.jsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import RelatedSectionShell, { TilesGrid } from './RelatedSectionShell';
import InsightTile from '../tiles/InsightTile';
import UnifiedInsightsTable from './tables/InsightsTable';
import { slugify } from '../../utils/slug';

/** Find the Insights section (title/description holder) */
const findInsightsSection = (sections) =>
  (sections || []).find((s) =>
    s?.title && /what ['’]?good['’]?\s*&\s*['’]?bad['’]?\s*look like/i.test(String(s.title))
  ) || null;

/** Collect ALL tiles provided by the dashboard selectors (keep order & props intact) */
function collectAllTiles(sec) {
  const subs = Array.isArray(sec?.subsections) ? sec.subsections : [];
  const fromSubs = subs.flatMap((ss) => (Array.isArray(ss?.tiles) ? ss.tiles : []));
  const direct = Array.isArray(sec?.tiles) ? sec.tiles : [];
  return [...direct, ...fromSubs];
}

/** Build majority sentiment per insight_category from Customer Stories for this Job
 * Rule: Negative bucket if (Negative + Wish List) >= Positive; otherwise Positive.
 */
function buildMajoritySentimentMap(customerStories = [], jobName) {
  const target = slugify(jobName || '');
  const countsByCat = new Map(); // slug -> {pos: n, negWish: n}

  for (const s of customerStories) {
    const jobs = Array.isArray(s?.jobs_to_be_done) ? s.jobs_to_be_done : [];
    const job = jobs.find((j) => slugify(j?.job_to_be_done_category || '') === target);
    if (!job) continue;
    const insights = Array.isArray(job?.insights) ? job.insights : [];
    for (const it of insights) {
      const cat = it?.insight_category || 'Uncategorized';
      const key = slugify(cat);
      if (!countsByCat.has(key)) countsByCat.set(key, { pos: 0, negWish: 0 });
      const overall = String(it?.insight_sentiment_overall || '').trim();
      if (overall === 'Positive') countsByCat.get(key).pos += 1;
      else if (overall === 'Negative' || overall === 'Wish List') countsByCat.get(key).negWish += 1;
    }
  }

  const majority = new Map(); // slug -> 'positive' | 'negative'
  countsByCat.forEach((v, key) => {
    majority.set(key, v.negWish >= v.pos ? 'negative' : 'positive');
  });
  return majority;
}

/** Section total = same interview total the dashboard uses for this section (if present) */
const sectionTotalFrom = (sec) =>
  sec?.section_total_interviews ?? sec?.sectionTotal ?? 0;

export default function RelatedInsights({ sections, customerStories = [], jobName }) {
  // Single memo -> safe hooks usage
  const memo = React.useMemo(() => {
    const section = findInsightsSection(sections);
    if (!section) {
      return { section: null, posTiles: [], negTiles: [], sectionTotal: 0 };
    }

    // 1) Get the exact tiles the dashboard produced (with all intensity props intact)
    const allTiles = collectAllTiles(section);

    // 2) Decide Positive/Negative per category using majority sentiment from stories
    const maj = buildMajoritySentimentMap(customerStories, jobName);

    // 3) Partition tiles by that majority, preserving original order & props
    const posTiles = [];
    const negTiles = [];
    for (const tile of allTiles) {
      const cat = tile.insight_category || tile.category || tile.title || '';
      const key = slugify(cat);
      const bucket = maj.get(key) || 'negative'; // default to negative if unknown

      // Keep all original tile props (intensity number, label, bar inputs, etc.)
      // but normalize variant to our bucket so colors match the classification
      const out = { ...tile, variant: bucket };
      if (bucket === 'positive') posTiles.push(out);
      else negTiles.push(out);
    }

    const sectionTotal = sectionTotalFrom(section);
    return { section, posTiles, negTiles, sectionTotal };
  }, [sections, customerStories, jobName]);

  const { section, posTiles, negTiles, sectionTotal } = memo;

  if (!section) return null;

  const top3Pos = posTiles.slice(0, 3);
  const top3Neg = negTiles.slice(0, 3);

  return (
    <RelatedSectionShell section={section}>
      {/* Top-3 Positive themes (from dashboard tiles; intensity preserved) */}
      {top3Pos.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Top positive themes
          </Typography>
          <TilesGrid
            tiles={top3Pos}
            renderTile={(t) => <InsightTile {...t} sectionTotal={sectionTotal} />}
          />
        </Box>
      )}

      {/* Top-3 Negative themes (Negative OR Wish List majority) */}
      {top3Neg.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Top negative themes
          </Typography>
          <TilesGrid
            tiles={top3Neg}
            renderTile={(t) => <InsightTile {...t} sectionTotal={sectionTotal} />}
          />
        </Box>
      )}

      {/* Unified table below – already powered by raw stories */}
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          All insights
        </Typography>
        <UnifiedInsightsTable customerStories={customerStories} jobName={jobName} />
      </Box>
    </RelatedSectionShell>
  );
}
